import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@wundo/ui';
import type { MeetingAttendeeRow } from './meetingAttendeeList.ts';
import { m } from '@/paraglide/messages.js';

export interface MeetingAttendeesDialogProps {
  open: boolean;
  /** Everyone on the meeting, organiser first — see meetingAttendeeList. */
  attendees: MeetingAttendeeRow[];
  onClose: () => void;
}

/**
 * Everyone who was invited, in full.
 *
 * The hero line names a handful and stops, so this is where the rest of a
 * two-hundred-person invitation lives. Each line carries the address as well as
 * the name, because on a meeting that size the name alone often does not say
 * which colleague it is — and the organiser is marked, since the hero line no
 * longer has room to say it.
 *
 * Read, never edited: who is coming belongs to the calendar, and nothing here
 * pretends otherwise. Response status is deliberately left out — it changes up
 * to the minute and a stale "maybe" beside a name is worse than no answer.
 */
export function MeetingAttendeesDialog({
  open,
  attendees,
  onClose,
}: MeetingAttendeesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>
            {m.meeting_attendees_title({ count: attendees.length })}
          </DialogTitle>
        </DialogHeader>

        {/*
          The list carries the scroll, not the dialog: the title stays put and
          the button to leave stays reachable however long the invitation is.
        */}
        <div className='max-h-80 overflow-y-auto px-5 py-4'>
          <ul className='flex flex-col'>
            {attendees.map((attendee) => {
              const beneath = secondLine(attendee);
              return (
                <li
                  key={attendee.email ?? attendee.label}
                  className='flex min-w-0 flex-col py-1.5'
                >
                  <span className='text-base text-ink'>{attendee.label}</span>
                  {beneath && <span className='text-xs text-ink-muted'>{beneath}</span>}
                </li>
              );
            })}
          </ul>
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={onClose}>
            {m.meeting_attendees_close()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/*
 * One muted line under the name, at most: the address, the role, or both. Two
 * hundred rows three lines tall is a list nobody scrolls to the end of, and a
 * row that repeats its own name as its address says nothing twice. Compared
 * without case: a label that is just the address comes back normalised, while
 * the address keeps whatever casing the invite used.
 */
function secondLine(attendee: MeetingAttendeeRow): string {
  const address =
    attendee.email?.toLocaleLowerCase() === attendee.label.toLocaleLowerCase()
      ? null
      : attendee.email;
  const role = attendee.organizer ? m.meeting_attendee_organizer() : null;
  return [address, role].filter((part) => part !== null).join(' · ');
}
